import React from 'react'
import { Link } from 'react-router-dom'
import strasse from './strasse.jpg'
import gogh from './gogh.jpg'
import './artists.css'

const PaletteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
)

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
)

const ARTISTS = [
  {
    id: 'kandinsky',
    name: 'Wassily Kandinsky',
    years: '1866–1944 · Moscow & Munich',
    badge: 'Pioneer of Abstract Art',
    image: strasse,
    alt: 'Wassily Kandinsky – Strasse (Murnau)',
    preset: 'Preset: Strasse & Expressive Chroma',
    palette: ['#eab308', '#2563eb', '#dc2626', '#059669', '#1e293b'],
    bio: 'Kandinsky was fascinated and stimulated by the mystery of color. He believed each hue lived by its own autonomous frequency and likened painting directly to composing music—proving that abstract geometry and brushstrokes could awaken the human soul without copying the physical world.',
    quote: 'Color is the keyboard, the eyes are the hammers, the soul is the piano with many strings. The artist is the hand which plays, touching one key or another, to cause vibrations in the soul.',
    studioNote: 'In Dionyziuz Studio, your photos are deconstructed into dynamic brush rhythms, rich expressionist contours, and luminous spectral tension inspired by Kandinsky\'s Strasse style.',
    cta: 'Transform with Kandinsky Style'
  },
  {
    id: 'vangogh',
    name: 'Vincent van Gogh',
    years: '1853–1890 · Netherlands & France',
    badge: 'Post-Impressionism',
    badgeClass: 'artistBadge--vangogh',
    quoteClass: 'artistQuoteBox--vangogh',
    noteClass: 'artistStudioNote--vangogh',
    btnClass: 'artistBtn--vangogh',
    image: gogh,
    alt: 'Vincent van Gogh Provençal artwork study',
    preset: 'Preset: Arles & Expressive Impasto',
    palette: ['#f59e0b', '#38bdf8', '#1d4ed8', '#84cc16', '#451a03'],
    bio: 'One of the most influential figures in the history of art, Van Gogh transformed personal emotion into turbulent, textured brushwork. His genius fully blossomed under the radiant light of Arles in 1888, where his color palette ignited with unprecedented warmth and kinetic energy.',
    quote: 'I dream of painting and then I paint my dream. There is no blue without yellow and without orange.',
    studioNote: 'In Dionyziuz Studio, experience your memories through the eyes of Vincent van Gogh—infusing ordinary photos with expressive impasto swirls and radiant golden-hour chroma.',
    cta: 'Transform with Van Gogh Style'
  }
]

const Artists = ({ user, loginClick }) => {
  return (
    <section className="artistsSection" id="artists">
      <div className="container">
        {/* Section Header */}
        <div className="artistsHeader">
          <span className="artistsKicker">Artistic Lineage &amp; Master Styles</span>
          <h2>Our Favorite Artists</h2>
          <p className="artistsLead">
            The aesthetic foundation of the Dionyziuz Creative Studio. Explore the legendary masters
            whose palettes, brushwork, and emotional resonance power our neural transformation models.
          </p>
        </div>

        {/* Artists Showcase Cards */}
        <div className="artistsList">
          {ARTISTS.map((artist, idx) => {
            const isReversed = idx % 2 === 1
            const action = (
              <Link to="/transformation" className={`artistBtn ${artist.btnClass || ''}`}>
                {artist.cta} <span className="artistBtnArrow" aria-hidden="true">&rarr;</span>
              </Link>
            )

            return (
              <article
                key={artist.id}
                className={`artistCard ${isReversed ? 'artistCard--reverse' : ''}`}
              >
                {/* Artwork Media Frame */}
                <div className="artistMedia">
                  <img src={artist.image} alt={artist.alt} loading="lazy" />
                  <div className="artistMediaBadge">
                    <span className="artistMediaLabel">{artist.preset}</span>
                    <div className="artistPalette" aria-label="Artist palette preview">
                      {artist.palette.map((color) => (
                        <span
                          key={color}
                          className="artistPaletteDot"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Artist Bio & Studio Context */}
                <div className="artistBody">
                  <div className="artistMetaHeader">
                    <span className={`artistBadge ${artist.badgeClass || ''}`}>
                      {artist.badge}
                    </span>
                    <h3 className="artistName">{artist.name}</h3>
                    <div className="artistYears">{artist.years}</div>
                  </div>

                  <p className="artistBio">{artist.bio}</p>

                  <blockquote className={`artistQuoteBox ${artist.quoteClass || ''}`}>
                    <p className="artistQuoteText">&ldquo;{artist.quote}&rdquo;</p>
                  </blockquote>

                  <div className={`artistStudioNote ${artist.noteClass || ''}`}>
                    <span className="artistStudioIcon" aria-hidden="true">
                      <PaletteIcon />
                    </span>
                    <span>
                      <strong>In the Studio:</strong> {artist.studioNote}
                    </span>
                  </div>

                  <div className="artistAction">
                    {action}
                  </div>
                </div>
              </article>
            )
          })}
        </div>

        {/* Instagram & Community Spotlight */}
        <div className="communitySpotlight">
          <div className="communityContent">
            <div className="communityPill">
              <span aria-hidden="true">&#9670;</span>
              <span>Community &amp; Gallery</span>
            </div>
            <h3 className="communityTitle">Check us out on Instagram</h3>
            <p className="communityText">
              Where we regularly showcase neural art transformations, community submissions,
              and previews of upcoming stylistic experiments created with the Dionyziuz Studio.
            </p>
          </div>
          <div className="communityAction">
            <a
              href="https://www.instagram.com/dionyziuz/"
              target="_blank"
              rel="noopener noreferrer"
              className="communityInstagramBtn"
            >
              <span className="communityInstagramIcon" aria-hidden="true">
                <InstagramIcon />
              </span>
              <span>@Dionyziuz</span>
              <span aria-hidden="true">&#8599;</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Artists
